const AVATAR_PALETTE = [
  { bg: "#DBEAFE", text: "#1D4ED8" },
  { bg: "#EDE9FE", text: "#6D28D9" },
  { bg: "#FFEDD5", text: "#C2410C" },
  { bg: "#FCE7F3", text: "#BE185D" },
  { bg: "#D1FAE5", text: "#047857" },
  { bg: "#E0E7FF", text: "#4338CA" },
  { bg: "#FEF3C7", text: "#B45309" },
  { bg: "#F3E8FF", text: "#7E22CE" },
];

export const REVOKE_ELIGIBLE_STATUSES = new Set([
  "issued",
  "notified",
  "printed",
  "printed_and_notified",
]);

export const getHolderInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const getAvatarColors = (seed = "") => {
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash + str.charCodeAt(i) * (i + 1)) % AVATAR_PALETTE.length;
  }
  return AVATAR_PALETTE[hash] || AVATAR_PALETTE[0];
};

export const isRevokeEligibleStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return REVOKE_ELIGIBLE_STATUSES.has(normalized);
};

export const isNotifyEligibleStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return normalized !== "revoked" && normalized !== "expired";
};

export const parseHoldersListPayload = (payload) => {
  const root =
    payload?.data && (payload.data.results || payload.data.count != null)
      ? payload.data
      : payload;

  return {
    count: root?.count ?? 0,
    next: root?.next ?? null,
    previous: root?.previous ?? null,
    results: Array.isArray(root?.results) ? root.results : [],
  };
};

export const parseHolderDetailsPayload = (payload) => {
  const root =
    payload?.holder ||
    payload?.summary ||
    payload?.credentials ||
    payload?.results
      ? payload
      : payload?.data || payload;

  const credentials = Array.isArray(root?.results)
    ? root.results
    : Array.isArray(root?.credentials)
      ? root.credentials
      : [];

  return {
    holder: root?.holder || null,
    summary: root?.summary || {
      total_credentials: 0,
      active: 0,
      expired: 0,
      revoked: 0,
      verifications: 0,
    },
    credentials,
    count: root?.count ?? credentials.length,
    next: root?.next ?? null,
    previous: root?.previous ?? null,
  };
};

export const getIssuanceStatusBadgeClass = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (
    normalized === "issued" ||
    normalized === "notified" ||
    normalized === "printed" ||
    normalized === "printed_and_notified" ||
    normalized === "active"
  ) {
    return "border border-cfActiveBorder bg-cfActiveBg text-cfActiveText";
  }
  if (normalized === "expired") {
    return "border border-gray-300 bg-gray-100 text-gray-600";
  }
  if (normalized === "revoked" || normalized === "failed") {
    return "border border-cfStatusBlockedText bg-cfStatusBGBlocked text-cfStatusBlockedText";
  }
  return "border border-gray-300 bg-gray-100 text-gray-600";
};

export const getIssuanceStatusLabel = (status, t) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  const statusKeys = {
    issued: "holders:status_issued",
    notified: "holders:status_notified",
    printed: "holders:status_printed",
    printed_and_notified: "holders:status_printed_and_notified",
    active: "holders:status_active",
    expired: "holders:status_expired",
    revoked: "holders:status_revoked",
    failed: "holders:status_failed",
  };

  const key = statusKeys[normalized];
  if (key) return t(key);
  return status || t("holders:na");
};

export const filterHoldersClientSide = (
  holders,
  { search = "", programme = "", batch = "" } = {},
) => {
  const query = search.trim().toLowerCase();
  return holders.filter((holder) => {
    if (programme && holder.programme_name !== programme) return false;
    if (batch && holder.batch_name !== batch) return false;
    if (!query) return true;

    const haystack = [
      holder.holder_name,
      holder.email,
      holder.programme_name,
      holder.batch_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
};
